/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import {HttpClient, HttpErrorResponse, HttpStatusCode} from '@angular/common/http';
import {formatDate} from '@angular/common';
import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {AuthenticationService} from '../core/authentication/authentication.service';
import {IndustryScenarioSegmentDetail} from '../models/industry-scenario-segment-detail.model';
import {SegmentSchemeMap} from '../models/segment-scheme-map.model';
import {Segment} from '../models/segment.model';
import {ConfigService} from './config.service';

@Injectable({
  providedIn: 'root'
})
export class SegmentSchemeMapService {

  // Constant to hold the endpoint value
  private readonly segmentSchemeMappingEndpoint: string;
  private filename: string;

  constructor(private readonly configService: ConfigService,
              private readonly http: HttpClient, private readonly authService: AuthenticationService) {
    this.segmentSchemeMappingEndpoint = `${this.configService.baseApiUrl}segment-schemes-map`;
  }

  /**
   * Creates a new segment scheme mapping.
   * @param segment The selected, unmapped segment.
   */
  createSegmentSchemeMap(segment: Segment): Observable<SegmentSchemeMap> {
    return this.http.post<any>(this.segmentSchemeMappingEndpoint, segment)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Deletes a segment scheme mapping.
   * @param segment The selected, mapped segment.
   */
  deleteSegmentSchemeMap(segment: Segment): Observable<Segment> {
    return this.http.request<any>('DELETE', this.segmentSchemeMappingEndpoint, {
      body: segment
    }).pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Updates the segment scheme maps provided
   * @param segmentsToUpdate Array of segments to update.
   */
  updateMapping(segmentsToUpdate: Segment[]): Observable<Segment> {
    return this.http.put<any>(this.segmentSchemeMappingEndpoint, segmentsToUpdate)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Calls endpoint to download the Segment Scheme report
   */
  getReport(): void {
    const req = new Request(this.segmentSchemeMappingEndpoint + '/report');
    const headers = req.headers;
    headers.append('Accept', 'application/vnd.ms-excel');
    headers.append('Access-Control-Expose-Headers', 'Content-Disposition');
    headers.append('Authorization', this.authService.getAuthorizationHeaderValue());
    fetch(req, {headers})
    .then((response) => {
      const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');

      this.filename = 'SegmentSchemes_' + currDate + '.xlsx';
      return response.blob();
    })
    .then((blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = this.filename;
      document.body.append(link);
      link.click();
      link.remove();
    }).catch((err) => {
      throwError(err);
    });
  }

  /**
   * Calls endpoint to check if segment mapping is in use in industry scenarios
   */
  getScenarioSegmentDetails(mappingId: number): Observable<IndustryScenarioSegmentDetail[]> {
    return this.http.get<IndustryScenarioSegmentDetail[]>(this.segmentSchemeMappingEndpoint + '/scenario-details/' + mappingId);
  }


  /**
   * Handles the errors produced by the backend
   * @param errorResponse The error produced by the API to be handled by the frontend
   * @return void
   */
  handleError(errorResponse: HttpErrorResponse): any {
    let errorMessage = 'An unexpected error occurred.';
    switch (errorResponse.status) {
      case HttpStatusCode.UnprocessableEntity:
      case HttpStatusCode.BadRequest: {
        if (errorResponse.error && errorResponse.error.message) {
          errorMessage = errorResponse.error.message;
        }
        break;
      }
    }
    return throwError(errorMessage);
  }
}
